import { useCompanyLabels } from '../../../context/CompanyContext';
import StoreSelect from '../../molecules/StoreSelect';
import FileUploadField from '../../molecules/FileUploadField';
import { isStoreIdField } from '../../../data/formFieldPresets';
import React, { useState } from 'react';
import './review.css';

const OPTION_CONTROLS = new Set(['select', 'radio', 'checkbox']);
// const OPTION_CONTROLS = new Set(['select', 'dropdown', 'radio', 'checkbox']);

function slug(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function questionLabel(question) {
  return String(
    question.label ||
    question.fieldLabel ||
    question.fieldName ||
    question.name ||
    ''
  ).trim();
}

function questionId(question) {
  return `review-${question.id ?? question.fieldKey ?? (slug(questionLabel(question)) || 'field')}`;
}

function optionInputId(baseId, option, index) {
  if (index === 0) return baseId;
  return `${baseId}-${slug(option) || index}`;
}

function controlKind(question) {
  const type = String(question.fieldType || '').toLowerCase();
  const key = String(question.fieldKey || questionLabel(question) || '').toLowerCase();

  if (type === 'file') return 'file';
  if (type === 'images') return 'images';
  if (type === 'email' || key.includes('email')) return 'email';
  // if (type === 'mobile') return 'mobile';
  if (type === 'textarea' || type === 'text area') return 'textarea';
  if (type === 'date') return 'date';
  if (type === 'date time' || type === 'datetime' || type === 'datetime-local') return 'datetime-local';
  if (type === 'time') return 'time';
  if (type === 'number' || type === 'mobile') return 'number';
  if (type === 'radio') return 'radio';
  if (type === 'checkbox' || type === 'multiselect') return 'checkbox';
  if (type === 'dropdown' || type === 'select' || type === 'custom') {
    return question.options?.length ? 'select' : 'text';
  }
  return 'text';
}

function usesOptions(kind) {
  return OPTION_CONTROLS.has(kind);
  // return OPTION_CONTROLS.has(kind) || kind === 'select';
}

function isWideQuestion(kind) {
  return kind === 'textarea' || kind === 'radio' || kind === 'checkbox';
}

function isPlainTextField(question) {
  const type = String(question.fieldType || '').toLowerCase();
  return type === 'text' || type === 'textfield';
}

function stripDigits(value) {
  return String(value || '').replace(/\d/g, '');
}

function keepDigits(value) {
  return String(value || '').replace(/[^\d]/g, '');
}

function inputPlaceholder(question, kind) {
  const label = String(questionLabel(question) || 'value').toLowerCase();
  if (kind === 'date') return 'mm/dd/yyyy';
  if (kind === 'select') return `Select ${label}`;
  if (kind === 'email') return 'Enter email';
  // if (kind === 'mobile') return 'Enter mobile number';
  return `Enter ${label}`;
}

function valueKey(question) {
  return String(question.fieldKey || question.id || questionLabel(question) || questionId(question));
}

function parentFieldFor(question, fields = []) {
  const dependsOn = String(question.dependsOn || '').trim();
  if (!dependsOn) return null;
  return fields.find((field) => {
    const label = questionLabel(field);
    const key = String(field.fieldKey || '');
    return label === dependsOn || key === dependsOn;
  }) || null;
}

function visibleOptionsFor(question, fields = [], values = {}) {
  const options = question.options || [];
  const parent = parentFieldFor(question, fields);
  if (!parent) return options;
  const mappings = question.parentMappings || {};
  const hasMapping = options.some((option) => Boolean(mappings[option]));
  if (!hasMapping) return options;
  const parentValue = String(values[valueKey(parent)] || '').trim();
  if (!parentValue) return options;
  return options.filter((option) => String(mappings[option] || '') === parentValue);
}

function emptyValueFor(kind) {
  return kind === 'checkbox' || kind === 'images' ? [] : '';
}

function ReviewControl({ question, options, value, onChange, storeList }) {
  // const { label } = useCompanyLabels();
  const id = questionId(question);
  const kind = controlKind(question);
  const current = value ?? emptyValueFor(kind);
  const commonProps = {
    id,
    name: String(question.fieldKey || questionLabel(question) || id),
    required: Boolean(question.required),
  };

  if (isStoreIdField(question) && Array.isArray(storeList)) {
    return <StoreSelect {...commonProps} storeList={storeList} value={current} onChange={onChange} />;
  }

  if (kind === 'radio') {
    return (
      <div className="review-options review-options--inline">
        {options.map((option, index) => {
          const optionId = optionInputId(id, option, index);
          return (
            <label className="review-option" htmlFor={optionId} key={option}>
              <input
                {...commonProps}
                id={optionId}
                type="radio"
                value={option}
                checked={current === option}
                onChange={(event) => onChange(event.target.value)}
              />
              <span>{option}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (kind === 'checkbox') {
    const selected = Array.isArray(current) ? current : [];
    return (
      <div className="review-options review-options--inline">
        {options.map((option, index) => {
          const optionId = optionInputId(id, option, index);
          return (
            <label className="review-option" htmlFor={optionId} key={option}>
              <input
                id={optionId}
                name={`${commonProps.name}[]`}
                type="checkbox"
                value={option}
                checked={selected.includes(option)}
                required={false}
                onChange={(event) => onChange(
                  event.target.checked
                    ? [...selected, option]
                    : selected.filter((item) => item !== option),
                )}
              />
              <span>{option}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (kind === 'textarea') {
    return (
      <textarea
        {...commonProps}
        className="review-control review-control--textarea"
        placeholder={inputPlaceholder(question, kind)}
        value={current}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (kind === 'select') {
    return (
      <select
        {...commonProps}
        className="review-control"
        value={current}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{inputPlaceholder(question, kind)}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (kind === 'file') {
    return (
      <FileUploadField
        {...commonProps}
        value={current}
        onChange={onChange}
        multiple
      />
    );
  }

  if (kind === 'images') {
    return (
      <FileUploadField
        {...commonProps}
        value={current}
        onChange={onChange}
        multiple
      />
    );
  }

  // Mobile used the same digit-only input as Number.
  // if (kind === 'mobile') {
  //   return (
  //     <input
  //       {...commonProps}
  //       className="review-control"
  //       type="number"
  //       inputMode="numeric"
  //       placeholder={inputPlaceholder(question, kind)}
  //       value={current}
  //       onChange={(event) => onChange(keepDigits(event.target.value))}
  //       onKeyDown={(event) => {
  //         if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-' || event.key === '.') {
  //           event.preventDefault();
  //         }
  //         if (event.key.length === 1 && !/\d/.test(event.key) && !event.ctrlKey && !event.metaKey) {
  //           event.preventDefault();
  //         }
  //       }}
  //       onPaste={(event) => {
  //         const pasted = event.clipboardData.getData('text');
  //         if (/^\d*$/.test(pasted)) return;
  //         event.preventDefault();
  //         onChange(keepDigits(pasted));
  //       }}
  //     />
  //   );
  // }

  if (kind === 'number') {
    return (
      <input
        {...commonProps}
        className="review-control"
        type="number"
        inputMode="numeric"
        placeholder={inputPlaceholder(question, kind)}
        value={current}
        onChange={(event) => onChange(keepDigits(event.target.value))}
        onKeyDown={(event) => {
          if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-' || event.key === '.') {
            event.preventDefault();
          }
          if (event.key.length === 1 && !/\d/.test(event.key) && !event.ctrlKey && !event.metaKey) {
            event.preventDefault();
          }
        }}
        onPaste={(event) => {
          const pasted = event.clipboardData.getData('text');
          if (/^\d*$/.test(pasted)) return;
          event.preventDefault();
          onChange(keepDigits(pasted));
        }}
      />
    );
  }

  if (isPlainTextField(question)) {
    return (
      <input
        {...commonProps}
        className="review-control"
        type="text"
        inputMode="text"
        placeholder={inputPlaceholder(question, kind)}
        value={current}
        onChange={(event) => onChange(stripDigits(event.target.value))}
        onKeyDown={(event) => {
          if (/^\d$/.test(event.key)) event.preventDefault();
        }}
        onPaste={(event) => {
          const pasted = event.clipboardData.getData('text');
          if (!/\d/.test(pasted)) return;
          event.preventDefault();
          onChange(stripDigits(pasted));
        }}
      />
    );
  }

  return (
    <input
      {...commonProps}
      className="review-control"
      type={kind}
      placeholder={inputPlaceholder(question, kind)}
      value={current}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export default function Review({ fields = [], storeList }) {
  const { fieldLabel } = useCompanyLabels();
  const [values, setValues] = useState({});

  const updateValue = (question, nextValue) => {
    const key = valueKey(question);
    setValues((current) => {
      const nextValues = { ...current, [key]: nextValue };
      fields.forEach((field) => {
        const parent = parentFieldFor(field, fields);
        if (!parent || valueKey(parent) !== key) return;
        const kind = controlKind(field);
        if (!usesOptions(kind)) return;
        const allowed = visibleOptionsFor(field, fields, nextValues);
        const childKey = valueKey(field);
        const childValue = nextValues[childKey];
        if (Array.isArray(childValue)) {
          nextValues[childKey] = childValue.filter((item) => allowed.includes(item));
        } else if (childValue && !allowed.includes(childValue)) {
          nextValues[childKey] = '';
        }
      });
      return nextValues;
    });
  };

  if (!fields.length) {
    return <p className="review-empty">No form fields added</p>;
  }

  return (
    <div className="review-form-card">
      <form className="review-form" aria-label="Form fields preview" onSubmit={(event) => event.preventDefault()}>
        <div className="review-form-grid">
          {fields.map((question) => {
            const kind = controlKind(question);
            const id = questionId(question);
            const options = usesOptions(kind) ? visibleOptionsFor(question, fields, values) : [];
            return (
              <div
                className={`review-question${isWideQuestion(kind) ? ' review-question--wide' : ''}`}
                key={question.id ?? question.fieldKey ?? questionLabel(question)}
              >
                <label className="review-label" htmlFor={id}>
                  {fieldLabel(question) || 'Untitled field'}
                  {question.required ? <span className="review-required">*</span> : null}
                </label>
                <ReviewControl storeList={storeList}
                  question={question}
                  options={options}
                  value={values[valueKey(question)] ?? emptyValueFor(kind)}
                  onChange={(nextValue) => updateValue(question, nextValue)}
                />
              </div>
            );
          })}
        </div>
      </form>
    </div>
  );
}
